import { useState } from 'react';
import { CREDIT_STATUS_LABELS, CREDIT_STATUSES, formatCurrency, Size, t, type CreditStatus } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { NumericInput, Select, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, patch } from './api';

/**
 * The Account standing tile's Edit (pieces 10 and 13): our terms with the
 * account, written together through the one credit path.
 *
 * 🔨 A blank credit limit is the Organization-wide default, never no credit
 * and never zero, so the field says which number it falls back to rather than
 * leaving an empty box to be read as *none*. 🔨 The limit only ever warns and
 * only a person sets Hold; the modal says both where the person can act on
 * them.
 */
export interface AccountStandingEdit {
  paymentTerms: string;
  /** The account's own limit in integer cents, or null where it takes the organization default. */
  creditLimit: number | null;
  creditStatus: CreditStatus;
  currencyCode: string;
  /** The organization default a blank limit resolves to, in integer cents. */
  defaultCreditLimit: number | null;
}

export interface AccountStandingModalProps {
  open: boolean;
  companyId: string;
  /** The values the page already read, so the form opens on what is on the screen. */
  initial: AccountStandingEdit;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  openerId?: string | null;
}

export function AccountStandingModal({ open, companyId, initial, apiBaseUrl = '', onCancel, onSaved, openerId = null }: AccountStandingModalProps) {
  const [paymentTerms, setPaymentTerms] = useState(initial.paymentTerms ?? '');
  const [creditLimit, setCreditLimit] = useState<number | null>(initial.creditLimit ?? null);
  const [creditStatus, setCreditStatus] = useState<CreditStatus>(initial.creditStatus ?? 'Good');
  const [error, setError] = useState<string | null>(null);
  const currencyCode = initial.currencyCode || 'USD';
  const fallback = typeof initial.defaultCreditLimit === 'number' ? initial.defaultCreditLimit : null;
  // The blank state names the number it resolves to, so nobody clears the box expecting no credit.
  const limitHint = fallback === null
    ? t('creditModal.usesDefaultUnknown')
    : `${t('creditModal.usesDefault')} ${formatCurrency({ amount: fallback, currencyCode })}.`;

  const save = async () => {
    setError(null);
    const answer = await patch(`${PAGES_PATH}/companies/${companyId}/credit`, { paymentTerms: paymentTerms.trim(), creditLimit, creditStatus }, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onSaved();
    onCancel();
  };

  return (
    <Modal
      open={open}
      title={t('creditModal.title')}
      subtitle={t('creditModal.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="account-standing"
      actions={[
        { kind: 'primary', label: t('creditModal.save'), onAct: () => void save(), disabled: paymentTerms.trim() === '' },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-account-standing-modal>
        {error && <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} />}
        <TextInput size={Size.Large} label={t('creditModal.paymentTerms')} value={paymentTerms} onChange={setPaymentTerms} required name="credit-payment-terms" />
        <NumericInput size={Size.Medium} label={t('creditModal.creditLimit')} kind="currency" value={creditLimit} onChange={setCreditLimit} hint={limitHint} name="credit-limit" />
        <Select size={Size.Medium}
          label={t('creditModal.creditStatus')}
          value={creditStatus}
          onChange={(next) => setCreditStatus(next as CreditStatus)}
          options={CREDIT_STATUSES.map((option) => ({ value: option, label: CREDIT_STATUS_LABELS[option] }))}
          dataAttribute={{ 'data-credit-field': 'status' }}
        />
        {/* 🔨 Hold blocks new sales orders and shipments and never safety or warranty work; a limit on its own only warns. Neither states a problem, so neither carries an action. */}
        <AlertBanner tone="inactive" message={creditStatus === 'Hold' ? t('creditModal.holdNotice') : t('creditModal.overLimitNotice')} />
      </div>
    </Modal>
  );
}
